// #192 RBAC-B (2026-08-21) — la superficie deleghe minima: la pagina Grants
// (/system/users/grants), il catalogo permessi reso e i ruoli (di sistema e
// custom) assegnabili alle credenziali. Radice PROPRIA `rbac`: lo spread di
// dictionaries.ts è shallow — un secondo root `users` qui sovrascriverebbe
// sections/system.ts (la nota di sections/realms.ts).

export const en = {
  rbac: {
    title: 'Grants',
    visibilityHint:
      'A grant delegates exactly one role to a principal on a scope selector. Creating a grant requires holding that role with grant right on a containing selector; revoking your own grants is always allowed, others’ only with the authority to recreate them.',
    principal: 'Principal',
    role: 'Role',
    scope: 'Scope selector',
    grantRight: 'Grant right',
    grantRightChip: 'grant right',
    effective: 'Effective',
    effectiveChip: 'effective',
    ineffectiveChip: 'not effective',
    createdBy: 'Granted by',
    createdAt: 'Granted at',
    grantId: 'Grant id',
    systemChip: 'system',

    createTitle: 'New grant',
    createSubmit: 'Grant',
    createWarning:
      'This widens what the principal can do: it delegates every permission of the role on the selected scope, effective immediately on every node.',
    userLabel: 'Principal (user@realm)',
    userFreeHint: 'e.g. mario.rossi@cluster-name',
    roleLabel: 'Role',
    scopeLabel: 'Scope selector',
    scopeHelp: 'Exact scope (/cluster/tenant) or recursive (/cluster/*). The whole-namespace /* is not grantable.',
    grantRightLabel: 'With grant right (the principal may delegate this role further)',
    grantRightWarning:
      'Grant right is delegation authority: the principal will be able to grant this role — and everything it contains — to others within this scope. This is the widest gesture on this page.',

    revokeTitle: 'Revoke grant of {name}',
    revokeSubmit: 'Revoke',
    revokeBody:
      'The principal loses this role on this scope immediately. Grants this principal delegated under it silently lose effect too (and regain it if the authority returns).',

    rolesTitle: 'Roles',

    // RBAC-C MINORI (#207): rolesHint/rolesDenied retired with the Grants
    // roles card — the Roles tab is the one seat.
    roleGrains: '{n} permissions',
    roleIncludes: 'Includes',

    catalogTitle: 'Permission catalog',
    catalogDenied: 'The permission catalog could not be read.',

    // RBAC-C (2026-08-23): the scope-selector KIND rendered as data (scopes
    // stay dormant by ratifica — no scope authoring anywhere).
    selectorExactChip: 'exact',
    selectorRecursiveChip: 'recursive',

    // RBAC-C: the role-authoring console (Roles tab).
    roleName: 'Name',
    rolePermissions: 'Permissions',
    roleScope: 'Scope',
    roleId: 'Role id',
    roleAuthoringHint:
      'A role is a named aggregate of permissions and other roles. Authoring custom roles requires the rbac.role.* permissions, and every included role must be one you hold (cover) on the role’s scope — the server refuses the rest with a speaking 403. System roles (tvstor-*) are code-defined and immutable.',
    roleCreateTitle: 'New role',
    roleCreateSubmit: 'Create role',
    roleEditTitle: 'Edit role {name}',
    roleNameLabel: 'Name (DNS-1123)',
    roleNameHelp: 'Lowercase letters, digits and hyphens; the tvstor- prefix is reserved for system roles.',
    roleNameLocked: 'The name is immutable by contract.',
    rolePermissionsLabel: 'Permissions from the catalog',
    roleFilterPlaceholder: 'Type to filter the catalog…',
    roleSelectedCount: '{n} selected',
    roleIncludesLabel: 'Included roles (each must be a role you cover)',
    roleContentHint:
      'A role needs at least one permission or one included role. Includes you do not cover on this scope are refused by the server with a speaking 403 — you cannot compose a role out of powers you do not hold.'
  }
}

export const it: typeof en = {
  rbac: {
    title: 'Deleghe',
    visibilityHint:
      'Un grant delega esattamente un ruolo a un principale su un selettore di scope. Creare un grant richiede di tenere quel ruolo con grant right su un selettore che lo contiene; revocare i propri grant è sempre permesso, gli altrui solo con l’autorità di ricrearli.',
    principal: 'Principale',
    role: 'Ruolo',
    scope: 'Selettore di scope',
    grantRight: 'Grant right',
    grantRightChip: 'grant right',
    effective: 'Effettivo',
    effectiveChip: 'effettivo',
    ineffectiveChip: 'non effettivo',
    createdBy: 'Concesso da',
    createdAt: 'Concesso il',
    grantId: 'Id del grant',
    systemChip: 'di sistema',

    createTitle: 'Nuova delega',
    createSubmit: 'Concedi',
    createWarning:
      'Questo gesto allarga ciò che il principale può fare: delega ogni permesso del ruolo sullo scope scelto, con effetto immediato su ogni nodo.',
    userLabel: 'Principale (utente@realm)',
    userFreeHint: 'es. mario.rossi@nome-cluster',
    roleLabel: 'Ruolo',
    scopeLabel: 'Selettore di scope',
    scopeHelp: 'Scope esatto (/cluster/tenant) o ricorsivo (/cluster/*). L’intero namespace /* non è concedibile.',
    grantRightLabel: 'Con grant right (il principale potrà delegare a sua volta questo ruolo)',
    grantRightWarning:
      'Il grant right è autorità di delega: il principale potrà concedere questo ruolo — e tutto ciò che contiene — ad altri dentro questo scope. È il gesto più ampio di questa pagina.',

    revokeTitle: 'Revoca la delega di {name}',
    revokeSubmit: 'Revoca',
    revokeBody:
      'Il principale perde subito questo ruolo su questo scope. Anche i grant che ha delegato sotto di esso perdono effetto in silenzio (e lo riacquistano se l’autorità torna).',

    rolesTitle: 'Ruoli',
    roleGrains: '{n} permessi',
    roleIncludes: 'Include',

    catalogTitle: 'Catalogo dei permessi',
    catalogDenied: 'Il catalogo dei permessi non è leggibile.',

    selectorExactChip: 'esatto',
    selectorRecursiveChip: 'ricorsivo',

    roleName: 'Nome',
    rolePermissions: 'Permessi',
    roleScope: 'Scope',
    roleId: 'Id del ruolo',
    roleAuthoringHint:
      'Un ruolo è un aggregato nominato di permessi e altri ruoli. L’authoring dei ruoli custom richiede i permessi rbac.role.*, e ogni ruolo incluso deve essere un ruolo che tieni (copri) sullo scope del ruolo — il resto il server lo rifiuta con un 403 parlante. I ruoli di sistema (tvstor-*) sono definiti nel codice e immutabili.',
    roleCreateTitle: 'Nuovo ruolo',
    roleCreateSubmit: 'Crea ruolo',
    roleEditTitle: 'Modifica il ruolo {name}',
    roleNameLabel: 'Nome (DNS-1123)',
    roleNameHelp: 'Minuscole, cifre e trattini; il prefisso tvstor- è riservato ai ruoli di sistema.',
    roleNameLocked: 'Il nome è immutabile per contract.',
    rolePermissionsLabel: 'Permessi dal catalogo',
    roleFilterPlaceholder: 'Scrivi per filtrare il catalogo…',
    roleSelectedCount: '{n} selezionati',
    roleIncludesLabel: 'Ruoli inclusi (ognuno dev’essere un ruolo che copri)',
    roleContentHint:
      'Un ruolo richiede almeno un permesso o un ruolo incluso. Gli include che non copri su questo scope il server li rifiuta con un 403 parlante — non si compone un ruolo con poteri che non si tengono.'
  }
}
